import { useCallback, useEffect, useMemo, useState } from "react";
import { ExperimentType } from "../Types";
import {
  parseInitialTags,
  parseTenantFilterAdvancedTags,
  serializeTenantFilterAdvanced,
  parseExtensionVersionFilters,
  serializeExtensionVersionFilters,
} from "../Experiment/ExperimentDetails/ExperimentDetailsForm/experimentFormDetails.utils";
import { ITag } from "@fluentui/react";
import {
  validate,
  EXPERIMENT_FIELDS,
  VARIANT_FIELDS,
  ADVANCED_FILTERS_FIELDS,
} from "../Experiment/ExperimentDetails/ExperimentDetailsForm/ExperimentDetailsValidation/experimentDetailsFormValidation";
import {
  isEmptyArray,
  isEmptyObject,
  stripAllSpecialChars,
} from "../Shared/shared.utils";
import { useEv2ExperimentParams } from "./UseCurrentExtension";
import debounce from "lodash/debounce";
import useAppInsights from "./UseAppInsights";
import { useAppContext } from "../Contexts";

interface UseExperimentFormProps {
  experiments: ExperimentType[];
  initialFormData: ExperimentType;
  setShowConfirmationModal: (show: boolean) => void;
  setErrors: (errors: Record<string, string[]>) => void;
  isClone: boolean;
  isCreate: boolean;
}

export function useExperimentForm({
  experiments,
  initialFormData,
  setShowConfirmationModal,
  setErrors,
  isClone,
  isCreate,
}: UseExperimentFormProps) {
  const { extensionName = "" } = useEv2ExperimentParams();
  const { extensionConfig } = useAppContext();
  const { trackOperationEvent } = useAppInsights("UseExperimentForm");
  const extensionPrefix = useMemo(
    () => getExtensionPrefix(extensionName),
    [extensionName],
  );

  const initialFormState: ExperimentType = useMemo(
    () => ({
      ...initialFormData,
      // Remove extension prefix from featureKey so prefix only shows in the prefix section
      featureKey: initialFormData.featureKey.replace(
        new RegExp(`^${extensionPrefix}`),
        "",
      ),
      enableExtensionPrefix:
        initialFormData.featureKey?.startsWith(extensionPrefix),
    }),
    [initialFormData, extensionPrefix],
  );
  const [formState, setFormState] = useState<ExperimentType>(initialFormState);
  const [saveWiggle, setSaveWiggle] = useState(false);
  const [tags, setTags] = useState<ITag[]>(
    parseInitialTags(formState.analysisTags),
  );

  // Advanced filters state
  const [tenantFiltersOperator, setTenantFiltersOperator] = useState<
    "IN" | "NOTIN"
  >(formState.tenantFilters?.operator || "IN");
  const [tenantFiltersList, setTenantFiltersList] = useState<ITag[]>(
    parseTenantFilterAdvancedTags(formState.tenantFilters),
  );

  const [extensionVersionFilters, setExtensionVersionFilters] = useState(() =>
    parseExtensionVersionFilters(formState.extensionVersionFilters),
  );

  const validateForm = useCallback(
    (
      updatedData: ExperimentType,
    ): { valid: boolean; cleanedData: ExperimentType } => {
      // Clean trailing whitespace from all string fields before validation and modal preview
      const cleanedData = Object.fromEntries(
        Object.entries(updatedData).map(([k, v]) =>
          typeof v === "string" ? [k, v.replace(/\s+$/, "")] : [k, v],
        ),
      ) as ExperimentType;
      const valid = validate({
        data: cleanedData,
        setErrors: (errors) => setErrors(errors),
        experiments: experiments,
        isCreate: isCreate,
        isClone: isClone,
        extensionPrefix: extensionPrefix,
        extensionConfig,
      });
      return { valid, cleanedData };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore tracking
    [
      experiments,
      isCreate,
      isClone,
      setErrors,
      extensionPrefix,
      extensionConfig,
    ],
  );

  const debouncedValidateAndTrack = useMemo(
    () =>
      debounce(
        (
          updated: ExperimentType,
          field: string,
          value: any,
          initialValue: any,
        ) => {
          const { valid } = validateForm(updated);
          trackOperationEvent({
            operation: "FormChange",
            function: "handleFieldChange",
            experimentName: formState.featureKey,
            properties: {
              field,
              value,
              initialValue,
              isClone,
              isCreate,
              initialFormState,
              updated,
              isValid: valid,
            },
          });
        },
        500,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allows debounce
    [],
  );

  const handleFieldChange = (field: string, value: any) => {
    let updated = { ...formState };
    let initialValue = initialFormState?.[field];
    // Remove field if value is empty
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      isEmptyArray(value) ||
      isEmptyObject(value)
    ) {
      delete updated[field];
    } else if (field === "analysisTags") {
      initialValue = tags;
      setTags(value);
      updated[field] = [(value as ITag[]).map((tag) => tag.name)];
    } else {
      updated[field] = value;
    }
    setFormState(updated);
    debouncedValidateAndTrack(updated, field, value, initialValue);
  };

  // Advanced filters handlers
  const handleTenantFiltersOperatorChange = (operator: "IN" | "NOTIN") => {
    setTenantFiltersOperator(operator);
    const updated = { ...formState };
    if (tenantFiltersList.length > 0) {
      updated.tenantFilters = serializeTenantFilterAdvanced(
        operator,
        tenantFiltersList,
      );
    } else {
      updated.tenantFilters = undefined;
    }
    setFormState(updated);
    const { valid } = validateForm(updated);
    trackOperationEvent({
      operation: "FormChange",
      function: "handleTenantFiltersOperatorChange",
      experimentName: formState.featureKey,
      properties: {
        field: "tenantFiltersOperator",
        value: operator,
        initialValue: tenantFiltersOperator,
        isClone,
        isCreate,
        initialFormState: initialFormState,
        updated,
        isValid: valid,
      },
    });
  };

  const handleTenantFiltersListChange = (items: ITag[]) => {
    setTenantFiltersList(items);
    const updated = { ...formState };
    if (items.length > 0) {
      updated.tenantFilters = serializeTenantFilterAdvanced(
        tenantFiltersOperator,
        items,
      );
    } else {
      updated.tenantFilters = undefined;
    }
    setFormState(updated);
    const { valid } = validateForm(updated);
    trackOperationEvent({
      operation: "FormChange",
      function: "handleTenantFiltersListChange",
      experimentName: formState.featureKey,
      properties: {
        field: "tenantFiltersList",
        value: items,
        initialValue: tenantFiltersList,
        isClone,
        isCreate,
        initialFormState: initialFormState,
        updated,
        isValid: valid,
      },
    });
  };

  const handleExtensionVersionFiltersOperatorChange = (operator: string) => {
    const newFilters = { ...extensionVersionFilters, operator };
    setExtensionVersionFilters(newFilters);
    const updated = { ...formState };
    updated.extensionVersionFilters = serializeExtensionVersionFilters(
      newFilters.operator,
      newFilters.version,
    );
    setFormState(updated);
    const { valid } = validateForm(updated);
    trackOperationEvent({
      operation: "FormChange",
      function: "handleExtensionVersionFiltersOperatorChange",
      experimentName: formState.featureKey,
      properties: {
        field: "extensionVersionFiltersOperator",
        value: operator,
        initialValue: extensionVersionFilters.operator,
        isClone,
        isCreate,
        initialFormState: initialFormState,
        updated,
        isValid: valid,
      },
    });
  };

  const debouncedHandleExtensionVersionFiltersVersionChange = useMemo(
    () =>
      debounce((version: string, updated: ExperimentType) => {
        const { valid } = validateForm(updated);
        trackOperationEvent({
          operation: "FormChange",
          function: "handleExtensionVersionFiltersVersionChange",
          experimentName: formState.featureKey,
          properties: {
            field: "extensionVersionFiltersVersion",
            value: version,
            initialValue: extensionVersionFilters.version,
            isClone,
            isCreate,
            initialFormState: initialFormState,
            updated,
            isValid: valid,
          },
        });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allows debounce
    [],
  );

  const handleExtensionVersionFiltersVersionChange = (version: string) => {
    const newFilters = { ...extensionVersionFilters, version };
    setExtensionVersionFilters(newFilters);
    const updated = { ...formState };
    updated.extensionVersionFilters = serializeExtensionVersionFilters(
      newFilters.operator,
      newFilters.version,
    );
    setFormState(updated);
    debouncedHandleExtensionVersionFiltersVersionChange(version, updated);
  };

  const handleEnvironmentFiltersChange = (
    stage: "RC" | "MPAC" | "Prod",
    items: string[],
  ) => {
    const updated = { ...formState };
    if (!items.length) {
      delete updated.environmentFilters?.[stage];
    } else {
      updated.environmentFilters = {
        ...updated.environmentFilters,
        [stage]: items,
      };
    }
    if (isEmptyObject(updated.environmentFilters)) {
      delete updated.environmentFilters;
    }
    setFormState(updated);
    const { valid } = validateForm(updated);
    trackOperationEvent({
      operation: "FormChange",
      function: "handleEnvironmentFiltersChange",
      experimentName: formState.featureKey,
      properties: {
        field: `environmentFilters.${stage}`,
        value: items,
        initialValue: formState.environmentFilters?.[stage],
        isClone,
        isCreate,
        initialFormState: initialFormState,
        updated,
        isValid: valid,
      },
    });
  };

  const handleSave = () => {
    const { valid, cleanedData } = validateForm(formState);
    if (!valid) {
      setSaveWiggle(true);
      setTimeout(() => setSaveWiggle(false), 600);
      return;
    }
    setFormState(cleanedData);
    setShowConfirmationModal(true);
    trackOperationEvent({
      operation: "Save",
      function: "handleSave",
      experimentName: formState.featureKey,
      properties: {
        formState: cleanedData,
      },
    });
  };

  const formChanges = useMemo(() => {
    // Helper to normalize empty values
    const normalizeValue = (value: any) => {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      return value;
    };

    // Helper to count changed fields
    const fieldsChangedCount = (fields: string[]) => {
      return fields.reduce((count, field) => {
        const initial = normalizeValue(initialFormState?.[field]);
        const current = normalizeValue(formState?.[field]);
        if (Array.isArray(initial) || Array.isArray(current)) {
          if (JSON.stringify(initial) !== JSON.stringify(current)) {
            return count + 1;
          }
        } else if (initial !== current) {
          return count + 1;
        }
        return count;
      }, 0);
    };
    const experimentChanges = fieldsChangedCount(EXPERIMENT_FIELDS);
    const variantChanges = fieldsChangedCount(VARIANT_FIELDS);
    const advancedFilterChanges = fieldsChangedCount(ADVANCED_FILTERS_FIELDS);
    const hasChanged =
      experimentChanges > 0 || variantChanges > 0 || advancedFilterChanges > 0;
    return {
      experimentChanges,
      variantChanges,
      advancedFilterChanges,
      hasChanged,
    };
  }, [formState, initialFormState]);

  // Update advanced filters when formState changes
  useEffect(() => {
    setTenantFiltersOperator(formState.tenantFilters?.operator || "IN");
    setTenantFiltersList(
      parseTenantFilterAdvancedTags(formState.tenantFilters),
    );
    setExtensionVersionFilters(
      parseExtensionVersionFilters(formState.extensionVersionFilters),
    );
  }, [formState.tenantFilters, formState.extensionVersionFilters]);

  // Update local state when experimentDetails changes (for pre-populating form)
  useEffect(() => {
    if (initialFormState && isCreate && !isClone) {
      setFormState(initialFormState);
    }
  }, [initialFormState, isCreate, isClone, setFormState]);

  return {
    formState,
    initialFormState,
    setFormState,
    handleSave,
    handleFieldChange,
    validateForm,
    tags,
    formChanges,
    saveWiggle,
    // Advanced filters state and handlers
    tenantFiltersOperator,
    tenantFiltersList,
    handleTenantFiltersOperatorChange,
    handleTenantFiltersListChange,
    extensionVersionFilters,
    handleExtensionVersionFiltersOperatorChange,
    handleExtensionVersionFiltersVersionChange,
    handleEnvironmentFiltersChange,
    extensionPrefix,
    experiments,
  };
}

export interface ExperimentFormContext {
  formState: ExperimentType;
  initialFormState: ExperimentType;
  setFormState: React.Dispatch<React.SetStateAction<ExperimentType>>;
  handleSave: () => void;
  handleFieldChange: (field: string, value: any) => void;
  validateForm: (updatedData: ExperimentType) => {
    valid: boolean;
    cleanedData: ExperimentType;
  };
  tags: ITag[];
  formChanges: {
    experimentChanges: number;
    variantChanges: number;
    advancedFilterChanges: number;
    hasChanged: boolean;
  };
  saveWiggle: boolean;
  // Advanced filters
  tenantFiltersOperator: "IN" | "NOTIN";
  tenantFiltersList: ITag[];
  handleTenantFiltersOperatorChange: (operator: "IN" | "NOTIN") => void;
  handleTenantFiltersListChange: (items: ITag[]) => void;
  extensionVersionFilters: { operator: string; version: string };
  handleExtensionVersionFiltersOperatorChange: (operator: string) => void;
  handleExtensionVersionFiltersVersionChange: (version: string) => void;
  handleEnvironmentFiltersChange: (
    stage: "RC" | "MPAC" | "Prod",
    items: string[],
  ) => void;
  extensionPrefix: string;
  experiments: ExperimentType[];
}

export const getExtensionPrefix = (extension: string) => {
  return `${stripAllSpecialChars(extension)}-` || "";
};
